import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import worksheetService from '../../services/worksheetService';
import { useConfirm } from '../../contexts/ConfirmContext';

const EXT_ICON = {
  '.pdf': '📄',
  '.jpg': '🖼️',
  '.jpeg': '🖼️',
  '.png': '🖼️',
  '.webp': '🖼️',
  '.gif': '🖼️',
};

const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

// Module-level cache so the list survives unmount/remount cycles when the
// component is hidden behind a tab (Share Resource <-> My Docs) or modal toggle.
// Stale entries are refreshed in the background on the next mount.
let cachedFiles = null;

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MyDocsLibrary = ({
  subject = '',
  grade = '',
  childId = '',
  batchId = '',
  emptyHint = 'Upload PDFs or images to use them as reference in worksheets, notes and videos.',
}) => {
  const [files, setFiles] = useState(() => cachedFiles || []);
  // Show the full loading state only on the very first ever load — subsequent
  // mounts already have a cached list to display while refreshing.
  const [loading, setLoading] = useState(() => cachedFiles === null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const fileInputRef = useRef(null);
  const confirm = useConfirm();

  const loadFiles = useCallback(async () => {
    if (cachedFiles === null) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');
    try {
      const res = await worksheetService.getReferenceFiles();
      const data = res.data?.files || [];
      cachedFiles = data;
      setFiles(data);
    } catch (e) {
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUpload = async (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length === 0) return;

    const invalid = selected.find((f) => {
      const ext = ('.' + (f.name.split('.').pop() || '')).toLowerCase();
      return !ALLOWED_EXTENSIONS.includes(ext);
    });
    if (invalid) {
      alert(`Only PDF, JPG, and PNG files are allowed. "${invalid.name}" is not supported.`);
      event.target.value = '';
      return;
    }

    setUploading(true);
    const duplicates = [];
    try {
      for (const file of selected) {
        const formData = new FormData();
        formData.append('file', file);
        if (subject) formData.append('subject', subject);
        if (grade) formData.append('grade', String(grade));
        if (childId) formData.append('child_id', childId);
        if (batchId) formData.append('batch_id', batchId);

        const resp = await axios.post('/worksheets/upload-reference', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (resp?.data?.duplicate) {
          duplicates.push(resp.data.filename || file.name);
        }
      }
      await loadFiles();
      if (duplicates.length === 1) {
        alert(`"${duplicates[0]}" is already in My Docs — using the existing copy.`);
      } else if (duplicates.length > 1) {
        alert(`These files were already in My Docs (existing copies kept):\n- ${duplicates.join('\n- ')}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error uploading file. Please try again.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDelete = async (file) => {
    const ok = await confirm({
      title: 'Delete document',
      message: `Delete "${file.original_filename || 'this document'}"? This cannot be undone.`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    setDeletingId(file.id);
    try {
      await axios.delete(`/worksheets/upload-reference/${file.id}`);
      setFiles((prev) => {
        const next = prev.filter((f) => f.id !== file.id);
        cachedFiles = next;
        return next;
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Unable to delete document. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  // Duplicate detection mirrors the server: docs already carrying a hash
  // group on hash, legacy docs without one fall back to (size + name).
  const duplicateCount = (() => {
    const seen = new Map();
    let dupes = 0;
    for (const f of files) {
      const key = f.file_hash || `${f.original_filename || ''}::${f.file_size_bytes || 0}`;
      if (seen.has(key)) {
        dupes += 1;
      } else {
        seen.set(key, true);
      }
    }
    return dupes;
  })();

  const handleCleanDuplicates = async () => {
    const ok = await confirm({
      title: 'Remove duplicate documents',
      message:
        'This keeps the oldest copy of each file and deletes the rest. Continue?',
      confirmLabel: 'Remove duplicates',
    });
    if (!ok) return;
    setCleaning(true);
    try {
      const res = await axios.post('/reference-files/dedupe');
      const removed = res?.data?.removed || 0;
      await loadFiles();
      if (removed > 0) {
        alert(`Removed ${removed} duplicate document${removed === 1 ? '' : 's'}.`);
      } else {
        alert('No duplicates found.');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Unable to remove duplicates. Please try again.');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Action bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-slate-50 flex-shrink-0">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>
            {loading ? 'Loading…' : `${files.length} document${files.length === 1 ? '' : 's'}`}
          </span>
          {refreshing && (
            <span className="inline-flex items-center gap-1 text-indigo-500">
              <span className="w-2.5 h-2.5 border border-indigo-300 border-t-indigo-500 rounded-full animate-spin" />
              Refreshing…
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {duplicateCount > 0 && (
            <button
              type="button"
              onClick={handleCleanDuplicates}
              disabled={cleaning || uploading}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                cleaning || uploading
                  ? 'bg-amber-50 text-amber-300 border-amber-100 cursor-not-allowed'
                  : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
              }`}
              aria-label="Remove duplicate documents"
            >
              {cleaning ? (
                <>
                  <span className="w-3 h-3 border-2 border-amber-300 border-t-amber-700 rounded-full animate-spin" />
                  Cleaning…
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                  </svg>
                  Remove {duplicateCount} duplicate{duplicateCount === 1 ? '' : 's'}
                </>
              )}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            className="hidden"
            onChange={handleUpload}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg text-white transition-colors ${
              uploading
                ? 'bg-indigo-300 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {uploading ? (
              <>
                <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Upload
              </>
            )}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2 bg-gray-50/40">
        {loading && files.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
            <span className="text-xs">Loading documents…</span>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
        {!loading && !error && files.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4">
            <span className="text-3xl mb-2">📁</span>
            <p className="text-sm font-medium text-gray-600">No documents uploaded yet</p>
            <p className="text-xs text-gray-400 mt-1">{emptyHint}</p>
          </div>
        )}
        {files.map((item) => {
          const ext = (item.file_extension || '').toLowerCase();
          const emoji = EXT_ICON[ext] || '📤';
          const name = item.original_filename || 'Unnamed file';
          const isDeleting = deletingId === item.id;
          return (
            <div
              key={item.id}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 flex items-center gap-3"
            >
              <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100 text-lg">
                {emoji}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-800 truncate">{name}</div>
                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 flex-wrap">
                  {item.subject && (
                    <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-100">
                      {item.subject}
                    </span>
                  )}
                  {item.grade && (
                    <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-600 border border-teal-100">
                      Gr.{String(item.grade).replace(/\D/g, '')}
                    </span>
                  )}
                  {item.file_size_bytes ? (
                    <span className="text-gray-400">{formatSize(item.file_size_bytes)}</span>
                  ) : null}
                  {item.extracted_content ? (
                    <span className="text-emerald-600">✓ Text extracted</span>
                  ) : (
                    <span className="text-gray-400">Image only</span>
                  )}
                  <span className="ml-auto text-gray-400">{formatDate(item.created_at)}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(item)}
                disabled={isDeleting}
                className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  isDeleting
                    ? 'bg-red-100 text-red-300 cursor-not-allowed'
                    : 'bg-red-50 text-red-600 hover:bg-red-100'
                }`}
                aria-label={`Delete ${name}`}
              >
                {isDeleting ? (
                  <span className="w-3 h-3 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                  </svg>
                )}
                Delete
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MyDocsLibrary;
